import type { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import axios from 'axios';

import { storage } from './storage';

import { config } from '@/config';

interface ApiClientConfig {
  baseURL?: string;
  timeout?: number;
  headers?: Record<string, string>;
}

type MapperFunction<T> = (data: unknown) => T;

class ApiClient {
  private instance: AxiosInstance;

  constructor(config: ApiClientConfig = {}) {
    this.instance = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 30000,
      headers: {
        'Content-Type': 'application/json',
        ...config.headers,
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    this.instance.interceptors.request.use(
      config => {
        if (window.location.pathname !== '/login') {
          const token = storage.getToken();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }
        return config;
      },
      error => {
        return Promise.reject(error);
      }
    );

    this.instance.interceptors.response.use(
      response => response,
      (error: AxiosError) => {
        if (error.response?.status === 401 && window.location.pathname !== '/login') {
          storage.removeToken();
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, mapper?: MapperFunction<T>, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.get(url, config);
    return mapper ? mapper(response.data) : response.data;
  }

  async post<T>(
    url: string,
    data?: unknown,
    mapper?: MapperFunction<T>,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.post(url, data, config);
    return mapper ? mapper(response.data) : response.data;
  }

  async put<T>(
    url: string,
    data?: unknown,
    mapper?: MapperFunction<T>,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.put(url, data, config);
    return mapper ? mapper(response.data) : response.data;
  }

  async patch<T>(
    url: string,
    data?: unknown,
    mapper?: MapperFunction<T>,
    config?: AxiosRequestConfig
  ): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.patch(url, data, config);
    return mapper ? mapper(response.data) : response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.instance.delete(url, config);
    return response.data;
  }

  getInstance(): AxiosInstance {
    return this.instance;
  }
}

const apiConfig: ApiClientConfig = {
  baseURL: config.apiBaseUrl,
};

export default new ApiClient(apiConfig);
export { ApiClient };
